import * as React from "react";
import { browserHistory } from "react-router";
import { updateEstimateWithToken, fetchWebOrderFromToken } from "../../tgccRequest";
import * as Tgcc from "../../tgccGlobal";
import { MajorAccountsHdr } from "../MajorAccountsHdr";
import { ErrorMessages } from "../../tgccGlobal";
import { EstimateErrorPopup, EstimateConfirmationPopup } from "../ErrorPopupModal";
import { getSharedEstimateErrorHeading } from "./estimateErrorHeading";

interface EstimateChangesProps {
    location: {
        query: {
            token?: string;
        };
    };
}

interface EstimateChangesState {
    webOrderNumber: string | null;
    notes: string;
    isLoading: boolean;
    errorMessage: string | null;
    isSubmitting: boolean;
    showConfirmation: boolean;
}

class EstimateChanges extends React.Component<EstimateChangesProps, EstimateChangesState> {
    private textareaRef = React.createRef<HTMLTextAreaElement>();

    constructor(props: EstimateChangesProps) {
        super(props);
        this.state = {
            webOrderNumber: null,
            notes: "",
            isLoading: true,
            errorMessage: null,
            isSubmitting: false,
            showConfirmation: false,
        };
    }

    componentDidMount() {
        // Ensure global styles are injected once (SSR-safe)
        if (typeof document !== 'undefined') {
            if (!document.getElementById(GLOBAL_STYLE_ID)) {
                const styleElement = document.createElement('style');
                styleElement.id = GLOBAL_STYLE_ID;
                styleElement.innerHTML = globalStyles;
                document.head.appendChild(styleElement);
            }
        }
        const { token } = this.props.location.query;

        if (!token) {
            this.setState({ 
                errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message, 
                isLoading: false,
            });
            return;
        }

        this.fetchOrderDetails(token);
    }

    fetchOrderDetails = (token: string) => {
        fetchWebOrderFromToken(
            token,
            (order) => {
                this.setState({ 
                    webOrderNumber: order.webOrderNumber, 
                    isLoading: false, 
                    errorMessage: null,
                });
            },
            (error) => {
                this.setState({ 
                    errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message, 
                    isLoading: false,
                });
                console.error("Error fetching webOrderNumber:", error);
            }
        );
    };

    handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newNotes = event.target.value.slice(0, 500);
        this.setState({ notes: newNotes });
    };

    handleSubmit = async () => {
        const { notes, webOrderNumber } = this.state;
        const { token } = this.props.location.query;

        if (!notes.trim()) {
            this.setState({ 
                errorMessage: "Please describe the changes you'd like to make before submitting.",
            });
            this.textareaRef.current?.focus();
            return;
        }

        if (!token || !webOrderNumber) {
            this.setState({ 
                errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message,
            });
            return;
        }

        this.setState({ isSubmitting: true });

        updateEstimateWithToken(
            token,
            "Changes",
            notes,
            (response) => {
                this.setState({ 
                    isSubmitting: false,
                    showConfirmation: true,
                });
            },
            (error) => {
                this.setState({ 
                    errorMessage: typeof error === "string" && error
                        ? error
                        : "We encountered an issue submitting your request. Please try again in a moment.",
                    isSubmitting: false,
                });
                console.error("Error updating estimate status:", error);
            }
        );
    };

    closeErrorPopup = () => {
        this.setState({ errorMessage: null });
    };

    closeConfirmationPopup = () => {
        this.setState({ showConfirmation: false });
        browserHistory.push(Tgcc.Routes.DashBoard);
    };

    getErrorHeading = (errorMessage: string | null): string => {
        if (!errorMessage) {
            return "Something went wrong";
        }

        // Check if it's the validation error
        if (errorMessage === "Please describe the changes you'd like to make before submitting.") {
            return "Validation Required";
        }

        const sharedHeading = getSharedEstimateErrorHeading(errorMessage);
        if (sharedHeading) {
            return sharedHeading;
        }

        // Default heading for unknown errors
        return "Error";
    };

    render() {
        const { webOrderNumber, notes, isLoading, errorMessage, isSubmitting, showConfirmation } = this.state;
        const characterCount = notes.length;
        const isNearLimit = characterCount > 450;

        return (
            <div style={styles.pageContainer}>
                {errorMessage && (
                    <EstimateErrorPopup 
                        message={errorMessage} 
                        heading={this.getErrorHeading(errorMessage)} 
                        onClose={this.closeErrorPopup} 
                    />
                )}
                
                {showConfirmation && (
                    <EstimateConfirmationPopup 
                        message="Your change request has been submitted successfully! Our team will review your request and get back to you within 1-2 business days."
                        heading="Request Submitted"
                        onClose={this.closeConfirmationPopup}
                    />
                )}
                
                <MajorAccountsHdr />
                
                <div style={styles.contentContainer}>
                    {/* Header Section */}
                    <div style={styles.header}>
                        <div style={styles.titleSection}>
                            <div style={styles.iconContainer}>
                                <span style={styles.icon}>✏️</span>
                            </div>
                            <div>
                                <h1 style={styles.title}>Request Changes</h1>
                                <p style={styles.subtitle}>
                                    Need adjustments? Let us know what should be modified.
                                </p>
                            </div>
                        </div>
                        
                        {!isLoading && webOrderNumber && (
                            <div style={styles.orderBadge}>
                                <span style={styles.badgeIcon}>📦</span>
                                Order {webOrderNumber}
                            </div>
                        )}
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner}></div>
                            <p style={styles.loadingText}>Loading your estimate details...</p>
                        </div>
                    ) : webOrderNumber ? (
                        <>
                            {/* Instructions */}
                            <div style={styles.card}>
                                <div style={styles.infoSection}>
                                    <div style={styles.infoIcon}>💡</div>
                                    <div>
                                        <h3 style={styles.infoTitle}>How to request changes</h3>
                                        <p style={styles.infoText}>
                                            Be specific about what needs to be changed - quantities, items, 
                                            specifications, or any other details. Our team will review your 
                                            request and get back to you promptly.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Input Section */}
                            <div style={styles.card}>
                                <label style={styles.label}>
                                    <span style={styles.labelText}>Change Request Details</span>
                                    <span style={styles.required}>*</span>
                                </label>
                                
                                <div style={styles.inputContainer}>
                                    <textarea
                                        ref={this.textareaRef}
                                        placeholder="Describe the changes you need... 
• Adjust quantity of [item]
• Change specifications for [feature]
• Add/remove specific items
• Update delivery requirements"
                                        style={{
                                            ...styles.textarea,
                                            borderColor: characterCount > 0 ? '#6557ff' : '#e6e6f0',
                                        }}
                                        value={notes}
                                        onChange={this.handleNotesChange}
                                        maxLength={500}
                                    />
                                    
                                    <div style={styles.inputFooter}>
                                        <div style={styles.characterCounter}>
                                            <span style={{
                                                ...styles.counterText,
                                                color: isNearLimit ? '#ff4757' : '#a5a5c2'
                                            }}>
                                                {characterCount}/500
                                            </span>
                                        </div>
                                        <div style={styles.suggestion}>
                                            💬 Be as detailed as possible for faster processing
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Section */}
                            <div style={styles.actionSection}>
                                <button 
                                    style={{
                                        ...styles.submitButton,
                                        ...(isSubmitting || !notes.trim() ? styles.submitButtonDisabled : {}),
                                    }}
                                    onClick={this.handleSubmit}
                                    disabled={isSubmitting || !notes.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div style={styles.buttonSpinner}></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <span style={styles.buttonIcon}>📬</span>
                                            Submit Change Request
                                        </>
                                    )}
                                </button>
                                
                                <div style={styles.helperText}>
                                    ⏳ Typically processed within 1-2 business days
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        );
    }
}

const styles: { [key: string]: React.CSSProperties } = {
    pageContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9ff 0%, #f6f2ff 100%)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        padding: "20px",
    },
    contentContainer: {
        width: "100%",
        maxWidth: "800px",
        marginTop: "100px",
        animation: "slideUp 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "2rem",
        gap: "2rem",
    },
    titleSection: {
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
        flex: 1,
    },
    iconContainer: {
        background: "linear-gradient(135deg, #6557ff 0%, #aa3fff 100%)",
        borderRadius: "16px",
        padding: "12px",
        fontSize: "1.5rem",
    },
    icon: {
        filter: "drop-shadow(0 2px 4px rgba(101, 87, 255, 0.3))",
    },
    title: {
        color: "#1a1a2e",
        fontSize: "2.5rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
        margin: "0 0 0.5rem 0",
        lineHeight: 1.2,
    },
    subtitle: {
        color: "#6b6b8a",
        fontSize: "1.1rem",
        lineHeight: 1.5,
        margin: 0,
        opacity: 0.9,
    },
    orderBadge: {
        background: "rgba(101, 87, 255, 0.1)",
        color: "#6557ff",
        padding: "0.75rem 1.25rem",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.95rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        border: "1px solid rgba(101, 87, 255, 0.2)",
        backdropFilter: "blur(10px)",
    },
    badgeIcon: {
        fontSize: "1.1rem",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem 2rem",
        textAlign: "center",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "4px solid #f3f0ff",
        borderLeft: "4px solid #6557ff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "1rem",
    },
    loadingText: {
        color: "#6b6b8a",
        fontSize: "1.1rem",
        margin: 0,
    },
    card: {
        background: "#ffffff",
        borderRadius: "20px",
        padding: "2rem",
        marginBottom: "1.5rem",
        boxShadow: "0 8px 32px rgba(101, 87, 255, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        backdropFilter: "blur(10px)",
    },
    infoSection: {
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
    },
    infoIcon: {
        fontSize: "1.5rem",
        marginTop: "0.25rem",
    },
    infoTitle: {
        color: "#2a2a48",
        fontSize: "1.2rem",
        fontWeight: 600,
        margin: "0 0 0.5rem 0",
    },
    infoText: {
        color: "#6b6b8a",
        lineHeight: 1.6,
        margin: 0,
    },
    label: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "1rem",
    },
    labelText: {
        color: "#2a2a48",
        fontSize: "1.1rem",
        fontWeight: 600,
    },
    required: {
        color: "#ff4757",
        fontSize: "1.2rem",
    },
    inputContainer: {
        position: "relative",
    },
    textarea: {
        width: "100%",
        minHeight: "200px",
        padding: "1.5rem",
        fontSize: "1rem",
        borderRadius: "16px",
        border: "2px solid #e6e6f0",
        background: "#fbfaff",
        resize: "vertical",
        transition: "all 0.3s ease",
        outline: "none",
        fontFamily: "inherit",
        lineHeight: 1.5,
    },
    inputFooter: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "0.75rem",
    },
    characterCounter: {
        color: "#a5a5c2",
        fontSize: "0.9rem",
    },
    counterText: {
        fontWeight: 500,
    },
    suggestion: {
        color: "#6557ff",
        fontSize: "0.9rem",
        fontWeight: 500,
        opacity: 0.8,
    },
    actionSection: {
        textAlign: "center",
        marginTop: "2rem",
    },
    submitButton: {
        background: "linear-gradient(45deg, #6557ff, #8b3fff)",
        color: "white",
        padding: "1rem 2.5rem",
        fontSize: "1.1rem",
        borderRadius: "14px",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.75rem",
        transition: "all 0.3s ease",
        boxShadow: "0 8px 25px rgba(101, 87, 255, 0.3)",
        fontWeight: 600,
        letterSpacing: "0.01em",
    },
    submitButtonDisabled: {
        opacity: 0.7,
        cursor: "not-allowed",
        transform: "none",
    },
    buttonIcon: {
        fontSize: "1.3rem",
        transition: "transform 0.2s ease",
    },
    buttonSpinner: {
        width: "18px",
        height: "18px",
        border: "2px solid transparent",
        borderLeft: "2px solid white",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    },
    helperText: {
        color: "#a5a5c2",
        fontSize: "0.9rem",
        marginTop: "1rem",
        fontWeight: 500,
    },
};

// Global animations (injected once via componentDidMount)
const GLOBAL_STYLE_ID = 'estimate-changes-global-styles';
const globalStyles = `
    @keyframes slideUp {
        0% {
            opacity: 0;
            transform: translateY(30px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
    
    textarea::placeholder {
        color: #c0c0d0;
        line-height: 1.5;
    }
    
    textarea:focus {
        border-color: #6557ff !important;
        box-shadow: 0 0 0 3px rgba(101, 87, 255, 0.1);
    }
    
    button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 35px rgba(101, 87, 255, 0.4);
    }
    
    button:active:not(:disabled) {
        transform: translateY(0);
    }
`;

export { EstimateChanges };